/**
 * Persistence + expiration de la session checkout événement /b/[slug].
 * Adapté de la version adhésion mais avec les champs spécifiques events
 * (selections, flexiblePrices, attendees, payerIndex, donationAmount, etc).
 */
import type { TicketType } from "~/types/event";

/**
 * Filtre les sélections sauvegardées en ne gardant que les types de billet
 * encore actifs. Retourne aussi `hasInvalid` si au moins une a été écartée.
 */
function filterValidSelections(
  savedSelections: Record<string, number> | undefined,
  ticketTypes: TicketType[],
): { validSelections: Record<string, number>; hasInvalid: boolean } {
  const validSelections: Record<string, number> = {};
  let hasInvalid = false;
  for (const [id, qty] of Object.entries(savedSelections || {})) {
    const tt = ticketTypes.find((t) => t.id === id);
    if (tt && tt.is_active) {
      validSelections[id] = qty as number;
    } else {
      hasInvalid = true;
    }
  }
  return { validSelections, hasInvalid };
}

/**
 * Normalise les participants sauvegardés : `selectedOptions` peut être un
 * tableau d'ids (legacy) → converti en map `{ id: 1 }`, sinon conservé.
 */
function normalizeSavedAttendees(attendees: unknown[]): unknown[] {
  // attendees est unknown[] après le narrowing Array.isArray — on type le
  // param en unknown puis on cast DANS le callback (TS strict refuse de
  // rétrécir le param directement).
  return attendees.map((raw: unknown) => {
    const a = raw as Record<string, unknown>;
    return {
      ...(a as object),
      selectedOptions: Array.isArray(a.selectedOptions)
        ? Object.fromEntries((a.selectedOptions as string[]).map((id: string) => [id, 1]))
        : ((a.selectedOptions as Record<string, number>) || {}),
    };
  });
}

interface SaveData {
  selections: Record<string, number>;
  flexiblePrices: Record<string, number>;
  attendees: unknown[];
  buyerInfo: unknown;
  payerIndex: number | null;
  includeFees: boolean;
  donateUs: boolean;
  donationAmount: number;
  showDonationInput: boolean;
  promoApplied: unknown;
}

interface RestoreTargets {
  selections: Ref<Record<string, number>>;
  flexiblePrices: Ref<Record<string, number>>;
  attendees: Ref<unknown[]>;
  buyerInfo: Ref<unknown>;
  payerIndex: Ref<number | null>;
  includeFees: Ref<boolean>;
  donateUs: Ref<boolean>;
  donationAmount: Ref<number>;
  showDonationInput: Ref<boolean>;
  promoApplied: Ref<unknown>;
  ticketTypes: Ref<TicketType[]>;
}

export function useEventCheckoutSession(
  slug: Ref<string>,
  checkoutStep: Ref<number>,
  restoreTargets: RestoreTargets,
  getSnapshot: () => SaveData,
) {
  const checkoutSession = useCheckoutSession(`event:${slug.value}`);
  const isRestoringSession = ref(true);
  const sessionExpiredModal = ref(false);

  function dismissSessionExpired() {
    sessionExpiredModal.value = false;
    checkoutStep.value = 1;
  }

  let expirationTimer: ReturnType<typeof setInterval> | null = null;
  if (import.meta.client) {
    onMounted(() => {
      expirationTimer = setInterval(() => {
        if (checkoutStep.value > 1 && checkoutSession.isExpired()) {
          checkoutSession.clear();
          sessionExpiredModal.value = true;
        }
      }, 5000);
    });
    onUnmounted(() => { if (expirationTimer) clearInterval(expirationTimer); });
  }

  function saveCheckoutState() {
    if (isRestoringSession.value) return;
    checkoutSession.syncStepToUrl(checkoutStep.value);
    if (checkoutStep.value <= 1) return;
    checkoutSession.save(checkoutStep.value, getSnapshot());
  }

  // Restaure les champs scalaires/optionnels (présents uniquement si définis
  // dans la sauvegarde). Extrait pour aplatir restoreCheckoutState.
  function restoreScalarFields(d: Partial<SaveData>) {
    if (d.flexiblePrices) restoreTargets.flexiblePrices.value = d.flexiblePrices;
    if (Array.isArray(d.attendees) && d.attendees.length) {
      restoreTargets.attendees.value = normalizeSavedAttendees(d.attendees);
    }
    if (d.buyerInfo) restoreTargets.buyerInfo.value = d.buyerInfo;
    if (d.payerIndex !== undefined) restoreTargets.payerIndex.value = d.payerIndex;
    if (d.includeFees !== undefined) restoreTargets.includeFees.value = d.includeFees;
    if (d.donateUs !== undefined) restoreTargets.donateUs.value = d.donateUs;
    if (d.donationAmount !== undefined) restoreTargets.donationAmount.value = d.donationAmount;
    if (d.showDonationInput !== undefined) restoreTargets.showDonationInput.value = d.showDonationInput;
    if (d.promoApplied) restoreTargets.promoApplied.value = d.promoApplied;
  }

  function restoreCheckoutState() {
    const saved = checkoutSession.restore();
    if (!saved) { isRestoringSession.value = false; return; }
    if (saved.expired) {
      isRestoringSession.value = false;
      sessionExpiredModal.value = true;
      return;
    }
    if (saved.step <= 1) { isRestoringSession.value = false; return; }
    const d = saved.data as Partial<SaveData>;
    // Verify ticket types still exist and have stock
    const { validSelections, hasInvalid } = filterValidSelections(
      d.selections,
      restoreTargets.ticketTypes.value,
    );
    if (Object.keys(validSelections).length === 0) {
      checkoutSession.clear();
      return;
    }
    restoreTargets.selections.value = validSelections;
    restoreScalarFields(d);
    checkoutStep.value = hasInvalid ? 1 : Math.min(saved.step, 3);
    checkoutSession.syncStepToUrl(checkoutStep.value);
    nextTick(() => { isRestoringSession.value = false; });
  }

  function clearCheckoutSession() {
    checkoutSession.clear();
  }

  return {
    sessionExpiredModal,
    dismissSessionExpired,
    saveCheckoutState,
    restoreCheckoutState,
    clearCheckoutSession,
    isRestoringSession,
  };
}
